import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { formatDate } from './SuperAdminShared';

const SuperAdminOverview = ({ stats, companies }) => {
  const statCards = [
    { title: 'Total Companies', value: stats.total_companies || 0, subtitle: 'Registered businesses on the platform' },
    { title: 'Active Companies', value: stats.active_companies || 0, subtitle: 'Subscriptions currently running' },
    { title: 'Active Plans', value: stats.active_packages || 0, subtitle: 'Packages available for signup' },
    {
      title: 'Top Selling Plan',
      value: stats.top_package?.package_name || 'No sales yet',
      subtitle: stats.top_package ? `${stats.top_package.sold_count} companies` : 'Waiting for first subscription',
    },
  ];

  const packageInsights = useMemo(() => {
    const counts = companies.reduce((acc, company) => {
      const name = company.package_name || 'No Package';
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [companies]);

  return (
    <section className="mt-6 space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {statCards.map((card) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg bg-white p-5 shadow-sm"
          >
            <div className="text-sm text-slate-500">{card.title}</div>
            <div className="mt-3 text-3xl font-semibold">{card.value}</div>
            <div className="mt-2 text-sm text-slate-600">{card.subtitle}</div>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.5fr_1fr]">
        <div className="rounded-lg bg-white p-5 shadow-sm">
          <h3 className="text-lg font-semibold">Recent company registrations</h3>
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="border-b text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Company</th>
                  <th className="px-3 py-2 text-left">Owner Email</th>
                  <th className="px-3 py-2 text-left">Plan</th>
                  <th className="px-3 py-2 text-left">Registered</th>
                  <th className="px-3 py-2 text-left">Expiry</th>
                </tr>
              </thead>
              <tbody>
                {companies.slice(0, 6).map((company) => (
                  <tr key={company.id} className="border-b last:border-b-0">
                    <td className="px-3 py-3">{company.company_name}</td>
                    <td className="px-3 py-3">{company.owner_email || '-'}</td>
                    <td className="px-3 py-3">{company.package_name || '-'}</td>
                    <td className="px-3 py-3">{formatDate(company.created_at)}</td>
                    <td className="px-3 py-3">{formatDate(company.subscription_end_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-lg bg-white p-5 shadow-sm">
          <h3 className="text-lg font-semibold">Package popularity</h3>
          <div className="mt-4 space-y-3">
            {packageInsights.length === 0 && (
              <div className="text-sm text-slate-500">No plan usage yet.</div>
            )}
            {packageInsights.map((item) => (
              <div key={item.name} className="rounded-lg border border-slate-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="font-medium">{item.name}</div>
                  <div className="text-sm text-slate-500">{item.count} companies</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SuperAdminOverview;
